/**
 * Shared display-formatting helpers for Helm.
 *
 * Pure, dependency-free, and intentionally small — the whole app reuses
 * these so rendering stays consistent (no per-screen ad-hoc UUID/date
 * formatting). Engine remains the source of truth for the values; these
 * only shape how they read in the UI.
 */

/**
 * Format an ISO timestamp as `YYYY-MM-DD`. Null / undefined / empty /
 * unparseable input renders the em-dash placeholder so callers never
 * have to null-check before display.
 */
export function formatDate(iso: string | null | undefined): string {
	if (!iso) return "—"
	const d = new Date(iso)
	if (Number.isNaN(d.getTime())) return "—"
	return d.toISOString().slice(0, 10)
}

/**
 * Render an ISO timestamp as a compact relative time: `"just now"`,
 * `"5m ago"`, `"3h ago"`, `"2d ago"`, then falls back to a short
 * `"Mon D"` calendar date beyond a week. Unparseable input renders empty
 * so callers never surface `NaN`. Shared across the notifications inbox and
 * activity feeds.
 */
export function relativeTime(iso: string): string {
	const date = new Date(iso)
	if (Number.isNaN(date.getTime())) return ""
	const sec = Math.round((Date.now() - date.getTime()) / 1000)
	if (sec < 45) return "just now"
	const min = Math.round(sec / 60)
	if (min < 60) return `${min}m ago`
	const hr = Math.round(min / 60)
	if (hr < 24) return `${hr}h ago`
	const day = Math.round(hr / 24)
	if (day < 7) return `${day}d ago`
	return date.toLocaleDateString(undefined, { month: "short", day: "numeric" })
}

/**
 * Humanize a SCREAMING_SNAKE engine enum into a sentence-case label:
 * `IN_TRANSIT` → `"In transit"`, `DRAFT` → `"Draft"`. Empty string
 * stays empty. Note: sentence case (only the first letter capitalised),
 * not per-word Title Case — matches the engine's status vocabulary.
 *
 * Display-only: callers must keep tone/colour mapping keyed on the RAW
 * enum, never on this output.
 */
export function humanizeStatus(s: string): string {
	if (!s) return ""
	const lower = s.toLowerCase().replace(/_/g, " ")
	return lower.charAt(0).toUpperCase() + lower.slice(1)
}

/**
 * Human-facing reference for a shipment: booking number, else BL number,
 * else the literal `"Draft"`. NEVER the raw UUID — a DRAFT shipment with
 * neither reference reads as "Draft", not an opaque id. Gates on
 * truthiness so an empty-string reference falls through.
 */
export function shipmentRef(s: {
	readonly bookingNumber?: string | null
	readonly blNumber?: string | null
}): string {
	if (s.bookingNumber) return s.bookingNumber
	if (s.blNumber) return s.blNumber
	return "Draft"
}
